import { Inject, Injectable, OnDestroy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import { AuthService, ProviderService, UiStateService } from '@app/core/services';
import { ApplicationInsightsService, AppInsightTags } from '@app/core/services/application-insights.service';
import { ConfigService } from '@app/core/services/config.service';
import { MerakiAuthService } from '@app/core/services/meraki-auth.service';
import { SidebarLocations } from '@app/core/services/state/ui-state/ui-state.store';
import { WINDOW } from '@app/core/services/window.provider';
import { BehaviorSubject, combineLatest, EMPTY, fromEvent, of, Subject, Subscription, timer } from 'rxjs';
import { catchError, concatMap, distinctUntilChanged, filter, map, pairwise, shareReplay, startWith, switchMap, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import * as uuidV4 from 'uuid/v4';
import {
  PracticeAgentAnalyticsDetails,
  PracticeAgentAnalyticsEventType,
  PracticeAgentHomeTab,
  PracticeAgentSessionEndReason,
} from './practice-agent-analytics.models';
import { PracticeAgentAnalyticsService } from './practice-agent-analytics.service';
import { PracticeAgentBehaviourService } from './practice-agent-behaviour.service';
import {
  PracticeAgentBehaviourEvent,
  PracticeAgentBehaviourRequest,
  PracticeAgentBehaviourResponse,
  PracticeAgentOpeningAttempt,
} from './practice-agent-behaviour.models';
import { PracticeAgentWidgetControlService } from './practice-agent-widget-control.service';
import {
  HbcAuthChangedMessage,
  HbcPracticeAgentMessage,
  HbcSuppressionChangedMessage,
  isPaNotificationCountChangedMessage,
  isPaSizeChangedMessage,
  isPaVisibilityChangedMessage,
  PaSizeChangedMessage,
  PaNotificationCountChangedMessage,
  PaVisibilityChangedMessage,
  PRACTICE_AGENT_MESSAGE_VERSION,
  PracticeAgentContext,
  PracticeAgentContextPayload,
  PracticeAgentPage,
  PracticeAgentSuppression,
  PracticeAgentSuppressionMode,
} from './practice-agent-message.models';

const AUTH_REFRESH_INTERVAL_MS = 50 * 60 * 1000;
const CONTEXT_RETRY_INTERVAL_MS = 60 * 1000;
const WIDGET_LOADING_TIMEOUT_MS = 60 * 1000;

type ContextMessageType = 'hbc.init' | 'hbc.context.changed';
type WidgetStatus = 'loading' | 'empty' | 'error' | null;

interface ContextMessageRequest {
  type: ContextMessageType;
  context: PracticeAgentContext;
  contextId: number;
  presentationMode: 'present' | 'refresh' | 'hidden';
}

interface BehaviourWork {
  request: PracticeAgentBehaviourRequest;
  generation: number;
  sentAt: number;
}

@Injectable()
export class PracticeAgentBridgeService implements OnDestroy {
  readonly widgetUrl = this.configService.config.practiceAgent.widgetUrl || '';

  private readonly widgetOrigin = this.getWidgetOrigin(this.widgetUrl);
  snoozeLabel = 'Snooze Practice Agent';
  private readonly paRequestedVisibilitySubject = new BehaviorSubject(false);
  private readonly suppressedSubject = new BehaviorSubject(false);
  private readonly expandedSubject = new BehaviorSubject(false);
  private readonly iframeReadySubject = new BehaviorSubject(false);
  private readonly widgetStatusSubject = new BehaviorSubject<WidgetStatus>(null);
  private readonly contextMessageRequests = new Subject<ContextMessageRequest>();
  private readonly subscriptions = new Subscription();
  private readonly behaviourRequests = new Subject<BehaviourWork>();
  private tabId = uuidV4();
  private sequence = 0;
  private generation = 0;
  private behaviourContextId = 0;
  private practiceTenantId = '';
  private attempt: PracticeAgentOpeningAttempt;
  private attemptDeadline = 0;
  private checkSubscription: Subscription;
  private expirySubscription: Subscription;
  private readyPending = false;
  private manualRequested = false;

  private iframe: HTMLIFrameElement;
  private latestContext: PracticeAgentContext;
  private authRefreshSubscription: Subscription;
  private contextRetrySubscription: Subscription;
  private widgetLoadingSubscription: Subscription;
  private suppressionMode: PracticeAgentSuppressionMode;
  private suppressionUntil: string;
  private currentContextId = 0;
  private nextContextId = 0;
  private enabled = false;
  private iframeLoaded = false;
  private initialContextSent = false;
  private started = false;
  private currentPage: PracticeAgentPage;
  private currentHomeTab: PracticeAgentHomeTab = 'other';

  private readonly firebaseAuthenticated$ = this.merakiAuthService.authState$.pipe(
    map(user => !!user),
    startWith(false),
    distinctUntilChanged()
  );

  private readonly widgetEnabled$ = this.providerService.provider$.pipe(
    map(provider => ({
      practiceTenantId: provider?.PracticeTenantId || '',
      practiceId: provider?.PracticeId || '',
    })),
    distinctUntilChanged(
      (previous, current) => previous.practiceTenantId === current.practiceTenantId && previous.practiceId === current.practiceId
    ),
    switchMap(() =>
      this.providerService.practiceAgentWidgetEnabled$.pipe(
        startWith(false),
        catchError(() => of(false))
      )
    )
  );

  private readonly providerPresent$ = this.providerService.provider$.pipe(
    map(provider => !!provider?.PracticeNumber && !!provider?.PracticeId),
    distinctUntilChanged()
  );

  readonly enabled$ =
    this.widgetOrigin && this.behaviourService.configured
      ? combineLatest([
          this.authService.authenticated$,
          this.firebaseAuthenticated$,
          this.providerPresent$,
          this.providerService.IsEnabledPracticeAgent$,
          this.widgetEnabled$,
        ]).pipe(
          map(
            ([authenticated, firebaseAuthenticated, providerPresent, practiceAgentEnabled, widgetEnabled]) =>
              authenticated && firebaseAuthenticated && providerPresent && !!practiceAgentEnabled && widgetEnabled
          ),
          distinctUntilChanged(),
          shareReplay({ bufferSize: 1, refCount: true })
        )
      : of(false);

  readonly visible$ = combineLatest([
    this.paRequestedVisibilitySubject,
    this.suppressedSubject,
    this.enabled$,
    this.widgetStatusSubject,
  ]).pipe(
    map(
      ([paRequestedVisibility, suppressed, enabled, widgetStatus]) => (paRequestedVisibility || !!widgetStatus) && !suppressed && enabled
    ),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  readonly expanded$ = this.expandedSubject.asObservable();
  readonly widgetStatus$ = this.widgetStatusSubject.asObservable();

  readonly sidebarOpen$ = this.uiStateService.sidebar$.pipe(
    map(sidebar => sidebar !== SidebarLocations.HIDDEN),
    distinctUntilChanged()
  );

  readonly wideSidebarOpen$ = this.uiStateService.sidebar$.pipe(
    map(sidebar => sidebar === SidebarLocations.CALENDAR),
    distinctUntilChanged()
  );

  private readonly currentPage$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((event: NavigationEnd) => event.urlAfterRedirects || event.url),
    startWith(''),
    map(url => url || this.router.url),
    tap(url => this.handleNavigation(url)),
    map(url => this.getCurrentPage(url)),
    distinctUntilChanged()
  );

  private readonly context$ = combineLatest([
    this.providerService.provider$,
    this.uiStateService.currentPatientId$,
    this.uiStateService.activePatients$,
    this.currentPage$,
  ]).pipe(
    map(([provider, currentPatientId, activePatients, currentPage]) => ({
      practiceNumber: provider?.PracticeNumber || '',
      doctorId: provider?.PracticeId || '',
      patientId: this.getContextPatientId(currentPage, currentPatientId, activePatients),
      currentPage,
    })),
    distinctUntilChanged(
      (previous, current) =>
        previous.practiceNumber === current.practiceNumber &&
        previous.doctorId === current.doctorId &&
        previous.patientId === current.patientId &&
        previous.currentPage === current.currentPage
    )
  );

  constructor(
    private authService: AuthService,
    private providerService: ProviderService,
    private uiStateService: UiStateService,
    private router: Router,
    private merakiAuthService: MerakiAuthService,
    private configService: ConfigService,
    private appInsightsService: ApplicationInsightsService,
    private analyticsService: PracticeAgentAnalyticsService,
    private behaviourService: PracticeAgentBehaviourService,
    private widgetControlService: PracticeAgentWidgetControlService,
    private snackBar: MatSnackBar,
    @Inject(WINDOW) private window: Window
  ) {}

  start(): void {
    if (this.started) {
      return;
    }

    this.started = true;

    if (this.widgetUrl && !this.widgetOrigin) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CONFIG_INVALID);
    }

    this.subscriptions.add(this.widgetControlService.openRequested$.pipe(tap(() => this.handleManualOpen())).subscribe());

    this.subscriptions.add(this.visible$.pipe(tap(visible => this.widgetControlService.setVisible(visible))).subscribe());
    this.subscriptions.add(
      combineLatest([this.enabled$, this.iframeReadySubject])
        .pipe(tap(([enabled, ready]) => this.widgetControlService.setAvailable(enabled && ready)))
        .subscribe()
    );

    this.subscriptions.add(
      this.behaviourRequests
        .pipe(
          concatMap(work =>
            this.behaviourService.send(work.request).pipe(
              tap(response => this.handleBehaviourResponse(work, response)),
              catchError(() => {
                this.handleBehaviourFailure(work);
                return EMPTY;
              })
            )
          )
        )
        .subscribe()
    );
    this.subscriptions.add(
      this.providerService.provider$
        .pipe(
          tap(provider => {
            const tenantId = provider?.PracticeTenantId || '';
            if (tenantId !== this.practiceTenantId) {
              this.resetOpening('provider-changed');
              this.practiceTenantId = tenantId;
            }
          })
        )
        .subscribe()
    );
    this.subscriptions.add(
      fromEvent(this.window.document, 'visibilitychange')
        .pipe(tap(() => this.handleAttentionChange('visibility')))
        .subscribe()
    );
    this.subscriptions.add(
      fromEvent(this.window, 'focus')
        .pipe(tap(() => this.handleAttentionChange('focus')))
        .subscribe()
    );
    this.subscriptions.add(
      fromEvent(this.window, 'blur')
        .pipe(tap(() => this.handleAttentionChange('focus')))
        .subscribe()
    );
    this.subscriptions.add(
      fromEvent(this.window, 'online')
        .pipe(tap(() => this.requestBehaviour('reconnect')))
        .subscribe()
    );

    this.subscriptions.add(
      this.authService.authenticated$
        .pipe(
          distinctUntilChanged(),
          pairwise(),
          filter(([wasAuthenticated, authenticated]) => wasAuthenticated && !authenticated),
          tap(() => this.handleLogout())
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled =>
            enabled
              ? this.contextMessageRequests.pipe(
                  switchMap(request =>
                    this.merakiAuthService.getFirebaseToken().pipe(
                      catchError(() => of(null)),
                      map(firebaseIdToken => ({ request, firebaseIdToken }))
                    )
                  )
                )
              : EMPTY
          ),
          tap(({ request, firebaseIdToken }) => this.sendContextMessage(request, firebaseIdToken))
        )
        .subscribe()
    );

    this.subscriptions.add(this.enabled$.pipe(tap(enabled => this.handleEnabledChange(enabled))).subscribe());

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled => (enabled ? this.context$ : EMPTY)),
          tap(context => this.handleContextChange(context))
        )
        .subscribe()
    );

    this.subscriptions.add(
      this.enabled$
        .pipe(
          switchMap(enabled => (enabled ? fromEvent<MessageEvent>(this.window, 'message') : EMPTY)),
          tap(event => this.handleMessage(event))
        )
        .subscribe()
    );
  }

  connectIframe(iframe: HTMLIFrameElement): void {
    if (!this.enabled) {
      return;
    }
    this.resetOpening('iframe-loaded');
    this.iframe = iframe;
    this.iframeLoaded = true;
    this.iframeReadySubject.next(true);
    this.initialContextSent = false;
    this.widgetControlService.setNotificationCount(null);
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_IFRAME_LOADED);
    this.startAuthRefresh();
    if (this.latestContext) {
      this.requestContextMessage('hbc.init', this.latestContext);
      this.requestBehaviour(this.currentPage === 'home' ? 'home-entered' : 'check');
    }
  }

  handleIframeError(): void {
    this.widgetControlService.setNotificationCount(null);
    this.resetOpening('iframe-load-failed');
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_IFRAME_LOAD_FAILED);
    this.stopAuthRefresh();
    this.stopContextRetry();
    this.iframe = null;
    this.iframeLoaded = false;
    this.iframeReadySubject.next(false);
    this.initialContextSent = false;
  }

  dismiss(): void {
    this.pauseAutomaticOpening('close');
  }

  snooze(): void {
    this.pauseAutomaticOpening('snooze');
  }

  destroy(): void {
    if (!this.started) {
      return;
    }

    this.started = false;
    this.widgetControlService.setVisible(false);
    this.widgetControlService.setEnabled(false);
    this.widgetControlService.setAvailable(false);
    this.disconnectIframe();
    this.subscriptions.unsubscribe();
  }

  ngOnDestroy(): void {
    this.destroy();
  }

  private handleEnabledChange(enabled: boolean): void {
    if (!enabled) {
      this.disconnectIframe();
    }
    this.enabled = enabled;
    this.widgetControlService.setEnabled(enabled);
  }

  private handleContextChange(context: PracticeAgentContext): void {
    const providerChanged =
      context.practiceNumber !== this.latestContext?.practiceNumber || context.doctorId !== this.latestContext?.doctorId;
    if (providerChanged || context.currentPage !== this.latestContext?.currentPage || context.patientId !== this.latestContext?.patientId) {
      this.resetOpening('context-changed', false);
      this.behaviourContextId++;
    }
    if (providerChanged) {
      this.widgetControlService.setNotificationCount(null);
      this.tabId = uuidV4();
      this.sequence = 0;
    }
    this.latestContext = context;
    const home = context.currentPage === 'home';
    if (!this.iframeLoaded) {
      return;
    }
    this.requestContextMessage(this.initialContextSent ? 'hbc.context.changed' : 'hbc.init', context);
    this.requestBehaviour(home ? 'home-entered' : 'check');
  }

  private requestContextMessage(type: ContextMessageType, context: PracticeAgentContext): void {
    this.stopContextRetry();
    this.contextMessageRequests.next(this.createContextMessageRequest(type, context));
  }

  private createContextMessageRequest(type: ContextMessageType, context: PracticeAgentContext): ContextMessageRequest {
    return {
      type,
      context,
      contextId: ++this.nextContextId,
      presentationMode: this.isEffectivelyVisible() ? 'refresh' : this.attempt ? 'present' : 'hidden',
    };
  }

  private sendContextMessage(request: ContextMessageRequest, firebaseIdToken: string): void {
    if (!this.canPostMessage() || request.contextId !== this.nextContextId) {
      return;
    }
    if (!firebaseIdToken) {
      const manual = this.manualRequested;
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_AUTH_FAILED);
      this.resetOpening('auth-failed');
      if (manual) {
        this.setWidgetStatus('error');
      }
      this.scheduleLatestContextRetry();
      return;
    }
    this.stopContextRetry();
    this.postMessage(this.createAuthMessage(firebaseIdToken));
    const payload: PracticeAgentContextPayload = {
      ...request.context,
      contextId: request.contextId,
      presentationMode: request.presentationMode,
      suppression: request.presentationMode === 'hidden' ? { suppressed: false } : this.getSuppression(),
    };
    this.postMessage({ type: request.type, version: PRACTICE_AGENT_MESSAGE_VERSION, payload });
    this.currentContextId = request.contextId;
    this.initialContextSent = true;
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CONTEXT_SENT, {
      messageType: request.type,
      currentPage: request.context.currentPage,
    });
  }

  private handleMessage(event: MessageEvent): void {
    if (!this.iframe || event.source !== this.iframe.contentWindow) {
      return;
    }

    if (event.origin !== this.widgetOrigin) {
      this.trackInvalidMessage('origin');
      return;
    }

    let message: PaVisibilityChangedMessage | PaSizeChangedMessage | PaNotificationCountChangedMessage;
    if (isPaVisibilityChangedMessage(event.data)) {
      message = event.data;
    } else if (isPaSizeChangedMessage(event.data)) {
      message = event.data;
    } else if (isPaNotificationCountChangedMessage(event.data)) {
      message = event.data;
    } else {
      this.trackInvalidMessage('payload');
      return;
    }

    if (message.payload.contextId !== this.currentContextId || this.currentContextId !== this.nextContextId) {
      this.trackInvalidMessage('stale-context');
      return;
    }

    if (message.type === 'pa.notifications.changed') {
      this.widgetControlService.setNotificationCount(message.payload.count);
      return;
    }

    if (message.type === 'pa.size.changed') {
      if (message.payload.expanded && !this.isEffectivelyVisible()) {
        return;
      }

      this.expandedSubject.next(message.payload.expanded);
      return;
    }

    const { visible, reason } = message.payload;
    if (visible) {
      if (this.isEffectivelyVisible() || this.readyPending || !this.attempt || this.suppressedSubject.value) {
        return;
      }
      if (!this.canPresent(this.attempt.mode) || Date.now() >= this.attemptDeadline) {
        this.resetOpening('preparation-expired');
        return;
      }
      this.readyPending = true;
      this.requestBehaviour('ready');
      return;
    }
    if (reason === 'card-api-failed') {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CARD_API_FAILED);
    }
    const manual = this.manualRequested;
    this.requestBehaviour(reason === 'no-relevant-card' ? 'empty' : 'failed');
    this.resetOpening(reason, false);
    if (manual) {
      this.setWidgetStatus(reason === 'no-relevant-card' ? 'empty' : 'error');
    }
  }

  private startAuthRefresh(): void {
    this.stopAuthRefresh();
    this.authRefreshSubscription = timer(AUTH_REFRESH_INTERVAL_MS, AUTH_REFRESH_INTERVAL_MS)
      .pipe(
        switchMap(() => this.merakiAuthService.getFirebaseToken().pipe(catchError(() => of(null)))),
        tap(firebaseIdToken => {
          if (!this.canPostMessage()) {
            return;
          }
          if (firebaseIdToken) {
            this.postMessage(this.createAuthMessage(firebaseIdToken));
          } else {
            this.resetOpening('auth-refresh-failed');
            this.postMessage(this.createAuthMessage(''));
            this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_AUTH_FAILED);
            this.scheduleLatestContextRetry();
          }
        })
      )
      .subscribe();
  }

  private stopAuthRefresh(): void {
    if (this.authRefreshSubscription) {
      this.authRefreshSubscription.unsubscribe();
      this.authRefreshSubscription = null;
    }
  }

  private scheduleContextRetry(request: ContextMessageRequest): void {
    this.stopContextRetry();
    this.contextRetrySubscription = timer(CONTEXT_RETRY_INTERVAL_MS)
      .pipe(
        filter(() => request.contextId === this.nextContextId && this.canPostMessage()),
        tap(() => this.contextMessageRequests.next(request))
      )
      .subscribe();
  }

  private scheduleLatestContextRetry(): void {
    if (!this.latestContext) {
      return;
    }

    const messageType = this.initialContextSent ? 'hbc.context.changed' : 'hbc.init';
    const request = this.createContextMessageRequest(messageType, this.latestContext);
    this.scheduleContextRetry(request);
  }

  private stopContextRetry(): void {
    if (this.contextRetrySubscription) {
      this.contextRetrySubscription.unsubscribe();
      this.contextRetrySubscription = null;
    }
  }

  private disconnectIframe(): void {
    this.resetOpening('disabled');
    this.stopAuthRefresh();
    this.stopContextRetry();
    this.checkSubscription?.unsubscribe();
    this.widgetControlService.setNotificationCount(null);
    this.iframe = null;
    this.iframeLoaded = false;
    this.iframeReadySubject.next(false);
    this.initialContextSent = false;
    this.latestContext = null;
    this.currentPage = null;
    this.currentHomeTab = 'other';
    this.suppressedSubject.next(false);
    this.suppressionMode = null;
    this.suppressionUntil = null;
  }

  private handleLogout(): void {
    this.disconnectIframe();
  }

  private trackAnalytics(eventType: PracticeAgentAnalyticsEventType, details: PracticeAgentAnalyticsDetails = {}): void {
    const analyticsDetails: PracticeAgentAnalyticsDetails =
      this.latestContext?.currentPage === 'home' ? { ...details, HomeTab: this.currentHomeTab } : details;
    this.subscriptions.add(
      this.analyticsService.track(eventType, this.latestContext?.currentPage || 'other', analyticsDetails).subscribe()
    );
  }

  private hideWidget(reason: string, sessionEndReason?: PracticeAgentSessionEndReason): void {
    const wasVisible = this.isEffectivelyVisible();
    this.setWidgetStatus(null);
    this.paRequestedVisibilitySubject.next(false);
    this.expandedSubject.next(false);
    if (wasVisible) {
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_HIDDEN, { reason });
      if (sessionEndReason) {
        this.trackAnalytics('widget-closed', { SessionEndReason: sessionEndReason });
      }
    }
  }

  private handleManualOpen(): void {
    if (
      !this.enabled ||
      !this.iframeLoaded ||
      !this.latestContext ||
      !this.isForeground() ||
      this.isEffectivelyVisible() ||
      this.widgetStatusSubject.value === 'loading'
    ) {
      return;
    }
    this.resetOpening('manual-open');
    this.suppressedSubject.next(false);
    this.manualRequested = true;
    this.setWidgetStatus('loading');
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_MANUAL_OPEN_REQUESTED);
    this.requestBehaviour('manual-open');
  }

  private setWidgetStatus(status: WidgetStatus): void {
    this.widgetLoadingSubscription?.unsubscribe();
    this.widgetStatusSubject.next(status);
    if (status === 'loading') {
      this.widgetLoadingSubscription = timer(WIDGET_LOADING_TIMEOUT_MS)
        .pipe(
          tap(() => {
            this.resetOpening('loading-timeout');
            this.setWidgetStatus('error');
          })
        )
        .subscribe();
    }
  }

  private handleNavigation(url: string): void {
    const nextPage = this.getCurrentPage(url);
    const nextHomeTab = this.getCurrentHomeTab(url);
    const sectionChanged = nextPage === 'home' && this.currentPage === 'home' && nextHomeTab !== this.currentHomeTab;
    this.currentPage = nextPage;
    this.currentHomeTab = nextHomeTab;
    if (sectionChanged) {
      this.behaviourContextId++;
      if (!this.isEffectivelyVisible()) {
        this.resetOpening('section-changed', false);
      }
      this.requestBehaviour('home-section-changed');
    }
  }

  private requestBehaviour(event: PracticeAgentBehaviourEvent, attemptId?: string): void {
    if (!this.enabled || !this.iframeLoaded || !this.latestContext?.doctorId || !this.practiceTenantId) {
      return;
    }
    const currentAttemptId = attemptId || this.attempt?.id;
    this.behaviourRequests.next({
      generation: this.generation,
      sentAt: Date.now(),
      request: {
        requestId: uuidV4(),
        practiceTenantId: this.practiceTenantId,
        practiceId: this.latestContext.doctorId,
        appId: 'clinical',
        tabId: this.tabId,
        sequence: ++this.sequence,
        event,
        context: {
          page: this.latestContext.currentPage,
          homeSection: this.currentHomeTab,
          visible: this.window.document.visibilityState === 'visible',
          focused: this.window.document.hasFocus(),
          contextId: this.behaviourContextId,
        },
        ...(currentAttemptId ? { attemptId: currentAttemptId } : {}),
      },
    });
  }

  private handleBehaviourResponse(work: BehaviourWork, response: PracticeAgentBehaviourResponse): void {
    if (this.isCurrentPauseRequest(work) && !response.actionResult) {
      this.reportPauseSaveFailure();
    }
    if (!this.enabled || work.generation !== this.generation || work.request.context.contextId !== this.behaviourContextId) {
      return;
    }
    this.snoozeLabel = response.policy.snoozeLabel;
    this.suppressionMode =
      response.automaticPause.kind === 'close' ? 'dismissed' : response.automaticPause.kind === 'snooze' ? 'snoozed' : null;
    this.suppressionUntil = response.automaticPause.until;
    this.scheduleBehaviourCheck(Date.parse(response.nextCheckAt) - Date.parse(response.serverTime));

    if (response.decision === 'prepare' || response.decision === 'show') {
      const remaining = Date.parse(response.attempt.expiresAt) - Date.parse(response.serverTime) - (Date.now() - work.sentAt);
      if (!this.canPresent(response.attempt.mode) || remaining <= 0) {
        this.requestBehaviour('cancel', response.attempt.id);
        this.resetOpening('opening-expired', false);
        return;
      }
      this.attempt = response.attempt;
      this.setAttemptDeadline(remaining);
      this.suppressedSubject.next(false);
      if (response.decision === 'prepare') {
        this.requestContextMessage(this.initialContextSent ? 'hbc.context.changed' : 'hbc.init', this.latestContext);
        return;
      }
      if (!this.readyPending || work.request.attemptId !== this.attempt.id) {
        return;
      }
      this.readyPending = false;
      this.paRequestedVisibilitySubject.next(true);
      this.setWidgetStatus(null);
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_CARD_AVAILABLE);
      this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_SHOWN);
      this.trackAnalytics('widget-shown');
      this.requestBehaviour('shown');
      return;
    }

    if (response.decision === 'keep' && this.attempt?.id === response.attempt?.id) {
      this.attempt = response.attempt;
      const remaining = response.attempt.expiresAt === null
        ? null
        : Date.parse(response.attempt.expiresAt) - Date.parse(response.serverTime) - (Date.now() - work.sentAt);
      this.setAttemptDeadline(remaining);
      return;
    }
    if (response.decision === 'hide') {
      this.resetOpening(response.reason, false);
      this.suppressedSubject.next(true);
      this.postMessage(this.createSuppressionMessage());
      this.requestContextMessage('hbc.context.changed', this.latestContext);
    }
    if (
      response.decision === 'none' &&
      this.attempt &&
      work.request.attemptId === this.attempt.id &&
      (response.attempt?.id !== this.attempt.id || ['expired', 'canceled', 'released', 'empty', 'failed'].includes(response.attempt.status))
    ) {
      const manual = this.manualRequested && !this.isEffectivelyVisible();
      this.resetOpening(response.reason, false);
      if (manual) {
        this.setWidgetStatus('error');
      }
    }
    if (work.request.event === 'manual-open' && !this.isEffectivelyVisible()) {
      this.setWidgetStatus('error');
    }
  }

  private handleBehaviourFailure(work: BehaviourWork): void {
    if (this.isCurrentPauseRequest(work)) {
      this.reportPauseSaveFailure();
    }
    if (work.generation !== this.generation || !this.enabled) {
      return;
    }
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_STATE_FAILED, { operation: work.request.event });
    if (work.request.event !== 'close' && work.request.event !== 'snooze' && this.manualRequested && !this.isEffectivelyVisible()) {
      this.resetOpening('backend-unavailable', false);
      this.setWidgetStatus('error');
    }
    this.scheduleBehaviourCheck(CONTEXT_RETRY_INTERVAL_MS);
  }

  private reportPauseSaveFailure(): void {
    this.snackBar.open(
      'Practice Agent closed here. We could not confirm the pause for your other tabs. Please try again later.',
      'OK'
    );
  }

  private isCurrentPauseRequest(work: BehaviourWork): boolean {
    return (
      this.enabled &&
      work.request.tabId === this.tabId &&
      work.request.practiceTenantId === this.practiceTenantId &&
      work.request.practiceId === this.latestContext?.doctorId &&
      (work.request.event === 'close' || work.request.event === 'snooze')
    );
  }

  private scheduleBehaviourCheck(delayMs: number): void {
    this.checkSubscription?.unsubscribe();
    this.checkSubscription = timer(Math.max(1000, delayMs))
      .pipe(tap(() => this.requestBehaviour('check')))
      .subscribe();
  }

  private setAttemptDeadline(remainingMs: number | null): void {
    this.expirySubscription?.unsubscribe();
    this.expirySubscription = null;
    this.attemptDeadline = remainingMs === null ? Infinity : Date.now() + remainingMs;
    if (remainingMs === null || (this.attempt.mode === 'manual' && this.isEffectivelyVisible())) {
      return;
    }
    this.expirySubscription = timer(Math.max(0, remainingMs))
      .pipe(
        tap(() => {
          const manual = this.manualRequested;
          this.resetOpening('opening-expired');
          if (manual) {
            this.setWidgetStatus('error');
          }
          this.requestBehaviour('check');
        })
      )
      .subscribe();
  }

  private pauseAutomaticOpening(event: 'close' | 'snooze'): void {
    if (!this.enabled) {
      return;
    }
    const attemptId = this.attempt?.id;
    this.resetOpening(event, false);
    this.suppressedSubject.next(true);
    this.suppressionMode = event === 'close' ? 'dismissed' : 'snoozed';
    this.suppressionUntil = null;
    this.postMessage(this.createSuppressionMessage());
    this.requestBehaviour(event, attemptId);
    this.trackEvent(event === 'close' ? AppInsightTags.PRACTICE_AGENT_WIDGET_DISMISSED : AppInsightTags.PRACTICE_AGENT_WIDGET_SNOOZED);
    this.trackAnalytics(event === 'close' ? 'widget-closed' : 'widget-snoozed', {
      SessionEndReason: event === 'close' ? 'doctor_close' : 'doctor_snooze',
    });
  }

  private resetOpening(reason: string, release = true): void {
    if (release && this.attempt) {
      this.requestBehaviour('release');
    }
    this.generation++;
    this.attempt = null;
    this.attemptDeadline = 0;
    this.readyPending = false;
    this.manualRequested = false;
    this.expirySubscription?.unsubscribe();
    this.stopContextRetry();
    this.hideWidget(reason);
    this.currentContextId = 0;
    this.nextContextId++;
  }

  private handleAttentionChange(event: 'focus' | 'visibility'): void {
    if (
      (this.attempt && !this.canPresent(this.attempt.mode) && !this.isEffectivelyVisible()) ||
      (this.attempt?.mode === 'automatic' && Date.now() >= this.attemptDeadline)
    ) {
      this.resetOpening('tab-attention-changed');
    }
    if (
      !this.attempt &&
      this.latestContext &&
      this.canPostMessage() &&
      this.window.document.visibilityState === 'visible' &&
      this.window.document.hasFocus()
    ) {
      this.requestContextMessage(this.initialContextSent ? 'hbc.context.changed' : 'hbc.init', this.latestContext);
    }
    this.requestBehaviour(event);
  }

  private isForeground(): boolean {
    return this.window.document.visibilityState === 'visible' && this.window.document.hasFocus();
  }

  private canPresent(mode: PracticeAgentOpeningAttempt['mode']): boolean {
    return this.isForeground() && (mode === 'manual' ? this.manualRequested : this.latestContext?.currentPage === 'home');
  }

  private isEffectivelyVisible(): boolean {
    return this.paRequestedVisibilitySubject.value && !this.suppressedSubject.value && this.enabled;
  }

  private createAuthMessage(firebaseIdToken: string): HbcAuthChangedMessage {
    return {
      type: 'hbc.auth.changed',
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload: { firebaseIdToken },
    };
  }

  private createSuppressionMessage(): HbcSuppressionChangedMessage {
    return {
      type: 'hbc.suppression.changed',
      version: PRACTICE_AGENT_MESSAGE_VERSION,
      payload: this.getSuppression(),
    };
  }

  private getSuppression(): PracticeAgentSuppression {
    if (!this.suppressedSubject.value) {
      return { suppressed: false };
    }

    return {
      suppressed: true,
      mode: this.suppressionMode,
      ...(this.suppressionUntil ? { until: this.suppressionUntil } : {}),
    };
  }

  private postMessage(message: HbcPracticeAgentMessage): void {
    if (this.canPostMessage()) {
      this.iframe.contentWindow.postMessage(message, this.widgetOrigin);
    }
  }

  private canPostMessage(): boolean {
    return this.enabled && this.iframeLoaded && !!this.iframe?.contentWindow && !!this.widgetOrigin;
  }

  private trackInvalidMessage(reason: string): void {
    this.trackEvent(AppInsightTags.PRACTICE_AGENT_WIDGET_INVALID_MESSAGE_REJECTED, { reason });
  }

  private trackEvent(event: AppInsightTags, properties: { [name: string]: string } = {}): void {
    this.appInsightsService.trackEvent(event, {
      ...properties,
      contractVersion: PRACTICE_AGENT_MESSAGE_VERSION.toString(),
    });
  }

  private getWidgetOrigin(widgetUrl: string): string | null {
    if (!widgetUrl) {
      return null;
    }

    try {
      const url = new URL(widgetUrl);
      const isDifferentOrigin = url.origin !== this.window.location.origin;
      const isLocalHttp = url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname);

      return isDifferentOrigin && (url.protocol === 'https:' || (!environment.production && isLocalHttp)) ? url.origin : null;
    } catch {
      return null;
    }
  }

  get validatedWidgetUrl(): string | null {
    return this.widgetOrigin ? this.widgetUrl : null;
  }

  private getContextPatientId(
    currentPage: PracticeAgentPage,
    currentPatientId: string,
    activePatients: Array<{ patientId: string }>
  ): string | null {
    const isPatientPage = ['patient-summary', 'patient-consultation', 'patient-record'].includes(currentPage);
    if (!isPatientPage || !currentPatientId) {
      return null;
    }

    return activePatients?.some(patient => patient.patientId === currentPatientId) ? currentPatientId : null;
  }

  private getCurrentPage(url: string): PracticeAgentPage {
    const path = (url || '').split(/[?#]/)[0];
    const segments = path.split('/').filter(segment => !!segment);
    const area = segments[1];

    if (area === 'home') {
      return 'home';
    }

    if (area === 'provider') {
      return 'provider';
    }

    if (area !== 'patient') {
      return 'other';
    }

    const patientSegment = segments[2];
    const patientPage = segments[3];
    if (!patientSegment || ['add', 'search', 'mobile-search'].includes(patientSegment)) {
      return 'patient-search';
    }

    if (patientPage === 'dashboard') {
      return 'patient-summary';
    }

    if (patientPage === 'consult') {
      return 'patient-consultation';
    }

    return 'patient-record';
  }

  private getCurrentHomeTab(url: string): PracticeAgentHomeTab {
    if (this.getCurrentPage(url) !== 'home') {
      return 'other';
    }

    try {
      const homeTab = new URL(url, this.window.location.origin).searchParams.get('tab') || 'schedule';
      const allowedTabs: PracticeAgentHomeTab[] = ['schedule', 'tracker', 'history', 'path', 'documents', 'whatsapp'];
      return allowedTabs.includes(homeTab as PracticeAgentHomeTab) ? (homeTab as PracticeAgentHomeTab) : 'other';
    } catch {
      return 'other';
    }
  }
}
